/**
 * CPT Description Mismatch Diagnostic (Jun 2026)
 *
 * Finds claim service lines whose stored CPT variant differs from the
 * variant chosen on their linked appointment (root cause of wrong
 * service descriptions on statements for pre-September claims) and
 * repairs them in bulk or selectively.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Search, Wrench, ArrowRight, FileWarning } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CptDescriptionDiagnostic() {
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const runScan = async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const res = await axios.get(`${API}/admin/diagnostics/cpt-description-mismatches?limit=500`, { headers: authHeaders() });
      setResult(res.data);
      if (!res.data.total_mismatches) toast.success('No CPT description mismatches found');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setLoading(false);
    }
  };

  const repair = async (claimIds) => {
    setRepairing(true);
    try {
      const body = claimIds ? { claim_ids: claimIds } : { apply_all: true };
      const res = await axios.post(`${API}/admin/diagnostics/cpt-description-mismatches/repair`, body, { headers: authHeaders() });
      toast.success(`Repaired ${res.data.repaired_lines} claim line(s)`);
      await runScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };

  const toggle = (claimId) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(claimId) ? n.delete(claimId) : n.add(claimId);
      return n;
    });
  };

  const items = result?.items || [];

  return (
    <Card data-testid="cpt-description-diagnostic">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileWarning className="h-5 w-5 text-amber-600" />
          CPT Description Mismatch Diagnostic
        </CardTitle>
        <p className="text-xs text-gray-500">
          Finds claim lines whose CPT variant/description doesn't match the linked note or appointment.
          Same-code description drifts are auto-repairable; lines where the claim billed a
          <b> different CPT code</b> than the note documents are flagged for review (report-only).
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={runScan} disabled={loading} data-testid="cpt-diag-scan-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
            Scan
          </Button>
          {items.length > 0 && (
            <>
              <Button size="sm" variant="outline" disabled={repairing || selected.size === 0}
                onClick={() => repair([...selected])} data-testid="cpt-diag-repair-selected-btn">
                <Wrench className="h-4 w-4 mr-1" />
                Repair Selected ({selected.size})
              </Button>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={repairing}
                onClick={() => { if (window.confirm(`Repair ALL ${result.total_mismatches} mismatched line(s)? Each line is re-validated against its appointment before writing.`)) repair(null); }}
                data-testid="cpt-diag-repair-all-btn">
                {repairing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Wrench className="h-4 w-4 mr-1" />}
                Repair All
              </Button>
            </>
          )}
          {result && (
            <Badge variant={result.total_mismatches ? 'destructive' : 'secondary'} data-testid="cpt-diag-count-badge">
              {result.total_mismatches} mismatch{result.total_mismatches === 1 ? '' : 'es'}
            </Badge>
          )}
        </div>

        {items.length > 0 && (
          <div className="max-h-96 overflow-y-auto border rounded">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="p-2 w-8"></th>
                  <th className="p-2 text-left">Client</th>
                  <th className="p-2 text-left">DOS</th>
                  <th className="p-2 text-left">CPT</th>
                  <th className="p-2 text-left">Current → Correct Description</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={`${it.claim_id}-${it.line_index}`} className="border-t hover:bg-gray-50" data-testid={`cpt-diag-row-${i}`}>
                    <td className="p-2">
                      {it.repairable === false ? (
                        <Checkbox checked={false} disabled data-testid={`cpt-diag-select-${i}`} />
                      ) : (
                        <Checkbox checked={selected.has(it.claim_id)} onCheckedChange={() => toggle(it.claim_id)}
                          data-testid={`cpt-diag-select-${i}`} />
                      )}
                    </td>
                    <td className="p-2 whitespace-nowrap">{it.client_name}</td>
                    <td className="p-2 whitespace-nowrap">{it.date_of_service}</td>
                    <td className="p-2 font-mono">
                      {it.mismatch_kind === 'code'
                        ? <span><span className="text-red-600 line-through">{it.cpt_code}</span> → <span className="text-green-700">{it.correct_cpt_code}</span></span>
                        : it.cpt_code}
                    </td>
                    <td className="p-2">
                      <span className="text-red-600 line-through">{it.current_description || '(blank)'}</span>
                      <ArrowRight className="h-3 w-3 inline mx-1 text-gray-400" />
                      <span className="text-green-700 font-medium">{it.correct_description}</span>
                      {it.mismatch_kind === 'code' && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200"
                          title="The claim billed a DIFFERENT CPT code than the note/appointment documents. Report-only — review the claim (a corrected claim may be needed); not auto-repairable."
                          data-testid="cpt-mismatch-kind-code">
                          code mismatch — review
                        </span>
                      )}
                      {it.expected_source && (
                        <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${it.expected_source === 'note' ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-600'}`}
                          title={it.expected_source === 'note' ? 'Correct CPT taken from the signed note header' : 'Correct CPT taken from the appointment (no note header CPT found)'}
                          data-testid="cpt-mismatch-source">
                          from {it.expected_source}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
